/* eslint-disable lyrahealth/no-react-native-text */
import React, { ReactElement } from 'react'

import { addDays, format } from 'date-fns'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { CalendarCardAppointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'

import ItemListPDF from './ItemListPDF'

// Extended type that includes subtitle as ReactElement
interface SessionAppointmentWithSubtitle extends Omit<CalendarCardAppointment, 'subtitle'> {
  subtitle?: ReactElement
}

type SessionsPDFProps = {
  pastAppointments: SessionAppointmentWithSubtitle[]
  providerEpisodesInfoPDF?: { text: string; id: string }[]
  pdfContentStartDate?: string
  pdfContentEndDate?: string
}

// const styles = StyleSheet.create({
//   sectionTitle: {
//     fontWeight: 'bold',
//     fontSize: 18,
//     color: lightThemeColors.textActive,
//     borderBottomWidth: 1,
//     borderBottomColor: lightThemeColors.textActive,
//     marginBottom: 20,
//     paddingBottom: 5,
//   },
//   subheader: {
//     fontSize: 16,
//     color: lightThemeColors.textPrimary,
//     marginBottom: 12,
//   },
//   emptyMessage: {
//     fontSize: 12,
//     color: lightThemeColors.textSecondary,
//     textAlign: 'center',
//     marginTop: 12,
//     padding: 12,
//     borderRadius: 8,
//     borderWidth: 1,
//     borderColor: lightThemeColors.borderDefault,
//   },
// })

const SessionsPDF = ({
  pastAppointments,
  providerEpisodesInfoPDF,
  pdfContentStartDate,
  pdfContentEndDate,
}: SessionsPDFProps): { [key: string]: React.ReactNode } => {
  const formatMeetingType = (meetingFormat: string): string => {
    switch (meetingFormat?.toLowerCase()) {
      case 'video':
        return 'Video'
      case 'phone':
        return 'Phone'
      case 'inperson':
        return 'In Person'
      case 'live_messaging':
        return 'Live Messaging'
      default:
        return meetingFormat || 'Unknown'
    }
  }

  const getSessionTitle = (appointment: SessionAppointmentWithSubtitle): string => {
    switch (appointment.appointmentStatus?.toLowerCase()) {
      case 'missed':
        return 'Missed Session'
      case 'canceled':
      case 'cancelled':
        return 'Canceled Session'
      default:
        return `Session ${appointment?.sessionNumber || ''}`
    }
  }

  const sessionsWithinDateRange = pastAppointments.filter((appointment) => {
    const sessionDate = getDateFromAppointment(appointment)
    return (
      (pdfContentStartDate ? sessionDate >= new Date(pdfContentStartDate) : true) &&
      (pdfContentEndDate ? sessionDate <= addDays(new Date(pdfContentEndDate), 1) : true)
    )
  })

  const sessionsByEpisode:
    | { text: string; id: string; appointments: SessionAppointmentWithSubtitle[] | undefined }[]
    | undefined = providerEpisodesInfoPDF
    ? providerEpisodesInfoPDF.map((episode) => {
        const appointmentsForEpisode = sessionsWithinDateRange.filter(
          (appointment) => appointment.episodeId === episode.id,
        )
        return {
          ...episode,
          appointments: appointmentsForEpisode.length ? appointmentsForEpisode : undefined,
        }
      })
    : undefined

  const renderedSessionsForEpisodes: { [key: string]: React.ReactNode } = {}

  if (sessionsByEpisode) {
    sessionsByEpisode.forEach((episode) => {
      if (!episode.appointments) {
        return
      }
      renderedSessionsForEpisodes[episode.text] = (
        <ItemListPDF
          items={episode.appointments.map((appointment) => {
            const sessionDate = getDateFromAppointment(appointment)
            const formattedDate = format(sessionDate, 'MMM d, yyyy')
            const formattedTime = format(sessionDate, 'h:mm a')
            const meetingType = formatMeetingType(appointment.meetingFormat || '')
            const duration = appointment.appointmentDuration
            return {
              title: getSessionTitle(appointment),
              details: [
                `${formattedDate} • ${formattedTime} ${appointment.timeZone}`,
                `${meetingType} • ${duration} min`,
              ],
            }
          })}
          showIndividually
        />
      )
    })
  }

  return renderedSessionsForEpisodes
}

export default SessionsPDF
